/**
 * The transport underneath the hand-kept mirror — the URL, the error, and the
 * two verbs. Everything in the sibling modules is a shape plus a call to one of
 * these.
 *
 * See `../http.ts` for what "hand-kept mirror" costs and why this half is not
 * generated.
 */

import { serverUrl } from "../serverOrigin.js";

/**
 * Where the API lives.
 *
 * Relative to the page whenever the page was served — which is every browser
 * case, and is what lets the `HttpOnly; SameSite=Lax` session and device cookies
 * ride along. `serverUrl` only makes it absolute for a page that has no origin
 * of its own (a packaged television app, phase 11).
 *
 * ⚠️ **A packaged app cannot use these routes as they stand, and phase 11a
 * measured why**: this server sends no `Access-Control-*` headers and answers a
 * preflight `OPTIONS` with 405, so a cross-origin `fetch` from a `file://` page
 * is refused by the browser before the response is read — and a `SameSite=Lax`
 * cookie would not have been sent even if it were not. That is not something an
 * absolute URL fixes; it is why phase 11c's pairing hands the television a
 * credential it holds *itself* rather than one the browser attaches.
 */
function api(path: string): string {
  return serverUrl(`/api${path}`);
}

/**
 * A clip, by content address.
 *
 * Goes on an `<audio src>`, which is **not** subject to CORS unless the element
 * asks to be, so this one URL does work cross-origin from a packaged app.
 * Unauthenticated by design (phase 5d): a digest says nothing about the song.
 */
export function clipUrl(sha256: string): string {
  return serverUrl(`/api/clip/${encodeURIComponent(sha256)}`);
}

/**
 * The stable error codes `src/http/error.rs` can return.
 *
 * Derived as a union from the object rather than written twice, so adding a code
 * is one edit.
 */
export const API_ERROR = {
  badRequest: "bad_request",
  invalidCredentials: "invalid_credentials",
  accountLocked: "account_locked",
  /**
   * Registered, but no admin has let them in yet — phase 19a, ADR 0021.
   *
   * ⚠️ **Only ever returned to somebody who typed the right password** (or held
   * a live emailed link), so a screen may say plainly that the account is
   * waiting. It is not an enumeration risk here for the same reason
   * `accountLocked` is not.
   */
  accountPending: "account_pending",
  /** An admin turned this account away. Distinct so the screen does not tell
   * somebody to keep waiting for a decision that has been made. */
  accountRejected: "account_rejected",
  badToken: "bad_token",
  unauthenticated: "unauthenticated",
  /**
   * Nothing at that address.
   *
   * **One code for "does not exist" and "not visible to you."** A recap answers
   * this for a game that is still running, deliberately indistinguishably from
   * one that never existed — so a screen must say *"this game is still going"*
   * as a possibility rather than as a fact it detected.
   */
  notFound: "not_found",
  /**
   * Too many requests. Carries a `Retry-After` header in seconds.
   *
   * Note where this does *not* appear: the mail-sending routes are rate limited
   * too and never say so, because a 429 there would reveal that somebody else
   * had recently asked for mail to an address. They answer normally and simply
   * do not send — so a screen must not try to detect it.
   */
  rateLimited: "rate_limited",
  internal: "internal",
} as const;

/** One of the codes above, or `"unknown"` for a response that carried none. */
export type ApiErrorCode = (typeof API_ERROR)[keyof typeof API_ERROR] | "unknown";

/** The body of any failed request. */
export interface ErrorBody {
  error: ApiErrorCode;
  message: string;
}

/**
 * A request the server refused.
 *
 * `message` is safe to render as-is: the server deliberately never puts an
 * internal cause in it (`src/http/error.rs`), so there is nothing to sanitise
 * and nothing worth hiding from the user.
 */
export class HttpError extends Error {
  readonly status: number;
  readonly code: ApiErrorCode;

  constructor(status: number, body: ErrorBody) {
    super(body.message);
    this.name = "HttpError";
    this.status = status;
    this.code = body.error;
  }
}

/** A simple acknowledgement. */
export interface Acknowledged {
  ok: boolean;
}

/** `GET /api/health` */
export interface Health {
  status: string;
  version: string;
}

/** Read a response, throwing [`HttpError`] for anything that is not a success. */
async function unwrap<T>(response: Response): Promise<T> {
  if (response.ok) {
    return (await response.json()) as T;
  }
  // A failure that is not JSON is a proxy or a crash rather than the API, and
  // the status is the only honest thing to report about it.
  const body = (await response.json().catch(() => null)) as ErrorBody | null;
  throw new HttpError(
    response.status,
    body ?? { error: "unknown", message: `request failed (${response.status})` },
  );
}

/**
 * `GET` a JSON endpoint.
 *
 * The session cookie is `HttpOnly`, so it is never read or set here — the
 * browser attaches it, and `same-origin` is what tells it to.
 */
export async function apiGet<T>(path: string): Promise<T> {
  const response = await fetch(api(path), {
    method: "GET",
    credentials: "same-origin",
  });
  return unwrap<T>(response);
}

/**
 * `POST` a JSON endpoint.
 *
 * `bearer` is for the one client that cannot use a cookie: a packaged
 * television holds its own device token (phase 11c) and presents it in a header,
 * over the deliberately credential-free CORS surface `src/http/mod.rs` opens for
 * it. A browser never passes one — its cookie is attached by the browser, and a
 * page that read a token to send would be defeating `HttpOnly`.
 */
export async function apiPost<T>(
  path: string,
  body: unknown,
  bearer?: string,
): Promise<T> {
  const response = await fetch(api(path), {
    method: "POST",
    credentials: "same-origin",
    headers: bearer
      ? { "Content-Type": "application/json", Authorization: `Bearer ${bearer}` }
      : { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return unwrap<T>(response);
}

/**
 * Send a JSON body with a method other than `POST`.
 *
 * `PATCH` for a partial update, `DELETE` where there is nothing to send. Kept
 * separate from [`apiPost`] rather than adding a method parameter to it: `POST`
 * is by far the common case and carries the bearer-token path that no other
 * method needs.
 */
export async function apiSend<T>(
  method: "PATCH" | "PUT" | "DELETE",
  path: string,
  body?: unknown,
): Promise<T> {
  const response = await fetch(api(path), {
    method,
    credentials: "same-origin",
    ...(body === undefined
      ? {}
      : {
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        }),
  });
  return unwrap<T>(response);
}

/**
 * Send raw bytes — an avatar, and nothing else so far.
 *
 * The body is the file itself rather than a multipart envelope. One file needs
 * no envelope, and multipart would mean a parser on the server for no gain.
 * `Content-Type` is deliberately **not** set from the `File`: the server sniffs
 * the format from the content, because a type the caller declares is not
 * evidence of anything.
 */
export async function apiPutBytes<T>(path: string, bytes: Blob): Promise<T> {
  const response = await fetch(api(path), {
    method: "PUT",
    credentials: "same-origin",
    body: bytes,
  });
  return unwrap<T>(response);
}
